import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  appraisalState,
  getAllAppraisals,
  updateAppraisal,
} from "./appraisalSlice";
import GridTable from "../../components/GridTable";
import { Button } from "../../components/Button";
import { showNotification } from "../../components/Notification";
import Dropdown from "../../components/Dropdown";
import { allUsers } from "../user/userSlice";
import { getTemplates } from "../template/templateSlice";
import { formatDateToDDMMYYYY, ROLES } from "../../uitls/Utils";
import useAuth from "../../hooks/useAuth";
import Swal from "sweetalert2";
import { deleteAppraisal, getAllAppraisal } from "../../api/appraisalApi";
import { getAllUsers } from "../../api/userApi";
import { getAllTemplates } from "../../api/templateApi";
import Loader from "../../components/Loader";

const ManagerReviewedAppraisals = () => {
  const { auth } = useAuth();
  const [editTemplate, setEditTemplate] = useState();
  const [viewTemplate, setViewTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState();
  const [submitReferenceState, setSubmitReferenceState] = useState();
  const [templateOptions, setTemplateOptions] = useState();
  const [employeeOptions, setEmployeeOptions] = useState();
  const [formValues, setFormValues] = useState();
  const dispatch = useDispatch();

  const allAppraisals = useSelector(getAllAppraisals);
  const stateOfAppraisal = useSelector(appraisalState);
  const allEmployees = useSelector(allUsers);
  const allTemplates = useSelector(getTemplates);

  useEffect(() => {
    dispatch(getAllAppraisal());
    dispatch(getAllUsers());
    dispatch(getAllTemplates());
  }, [dispatch]);

  useEffect(() => {
    if (!stateOfAppraisal.error && submitReferenceState) {
      resetStates();
    }
    setSubmitReferenceState(false);
  }, [submitReferenceState]);

  useEffect(() => {
    const employeesList = allEmployees
      ?.filter((employee) => employee?.managerId === auth?.user?.id)
      ?.map((employee) => ({
        label: employee?.firstName,
        value: employee?.id,
      }));
    setEmployeeOptions(employeesList);
    const templatesList = allTemplates?.map((template) => ({
      label: template?.templateTitle,
      value: template?.id,
    }));
    setTemplateOptions(templatesList);
  }, [allEmployees, allTemplates]);

  const columns = [
    {
      field: "s_no",
      headerName: "S.No",
      width: 100,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "employeeName",
      headerName: "Employee Name",
      width: 200,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "templateName",
      headerName: "Template Name",
      width: 300,
      headerClassName: "super-app-theme--header",
    },

    {
      field: "createdBy",
      headerName: "Created By",
      width: 200,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "createdDate",
      headerName: "Created Date",
      width: 200,
      headerClassName: "super-app-theme--header",
    },
    {
      field: "updatedDate",
      headerName: "Updated Date",
      width: 200,
      headerClassName: "super-app-theme--header",
    },
  ];

  // Format rows directly from Redux state
  const formattedRows = React.useMemo(() => {
    if (!Array.isArray(allAppraisals)) return [];

    const { employee, template } = formValues || {};

    let filteredAppraisals = allAppraisals;

    // Apply filters if they exist
    if (employee?.value || template?.value) {
      filteredAppraisals = allAppraisals.filter((appraisal) => {
        const isEmployeeMatch = employee?.value
          ? appraisal?.user?.id === employee.value
          : true;
        const isTemplateMatch = template?.value
          ? appraisal?.appraisalTemplate?.id === template.value
          : true;
        return isEmployeeMatch && isTemplateMatch;
      });
    }

    const filtered = filteredAppraisals
      ?.filter(
        (appraisal) =>
          appraisal?.user?.managerId === auth?.user?.id &&
          auth?.user?.userRoleId === ROLES?.mgr &&
          appraisal?.createdBy !== appraisal?.user?.id
      );
    
    // Sort by createdDate descending (newest first)
    const sorted = filtered?.sort((a, b) => {
      const dateA = new Date(a.createdDate || 0);
      const dateB = new Date(b.createdDate || 0);
      return dateB - dateA; // Descending order (newest first)
    });
    
    return sorted?.map((appraisal, index) => ({
      ...appraisal,
      s_no: index + 1,
      employeeName: `${appraisal?.user?.firstName} ${appraisal?.user?.lastName}`,
      templateName: appraisal?.appraisalTemplate?.templateTitle,
      createdBy: allEmployees?.find(
        (employee) => employee?.id === appraisal?.createdBy
      )?.firstName,
      createdDate: formatDateToDDMMYYYY(appraisal?.createdDate || null),
      updatedDate: formatDateToDDMMYYYY(appraisal?.updatedDate || null),
    }));
  }, [
    allAppraisals,
    allEmployees,
    auth?.user?.id,
    auth?.user?.userRoleId,
    formValues,
  ]);

  const resetStates = () => {
    setEditTemplate(false);
    setViewTemplate(false);
  };

  const trimObjectValues = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(trimObjectValues);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.keys(obj).reduce((acc, key) => {
        acc[key] = trimObjectValues(obj[key]);
        return acc;
      }, {});
    } else if (typeof obj === "string") {
      return obj.trim();
    }
    return obj;
  };
  const validateSelectedOptionIds = () => {
    const hasEmptyOptionId = selectedTemplate?.appraisalQuestionAnswer?.some(
      (obj) => obj?.selectedOptionId === "" || obj?.selectedOptionId === null
    );

    if (hasEmptyOptionId) {
      alert("Please select an option for all questions.");
      return false;
    }

    return true;
  };

  const handleEditAppraisal = () => {
    if (validateSelectedOptionIds()) {
      dispatch(updateAppraisal(trimObjectValues(selectedTemplate)));
      resetStates();
    }
  };

  const handleRadioChange = (e, questionId, optionId) => {
    setSelectedTemplate((prevValues) => ({
      ...prevValues,
      appraisalQuestionAnswer: prevValues?.appraisalQuestionAnswer?.map((obj) =>
        obj?.appraisalQuestions?.id === questionId
          ? {
              ...obj,
              selectedOptionId: optionId,
              comment: obj.comment,
            }
          : obj
      ),
    }));
  };

  const handleCheckBoxChange = (e, questionId, optionId) => {
    //for single true entry
    if (e.target.checked) {
      setSelectedTemplate((prevValues) => ({
        ...prevValues,
        appraisalQuestionAnswer: prevValues?.appraisalQuestionAnswer?.map(
          (obj) =>
            obj?.appraisalQuestions?.id === questionId
              ? {
                  ...obj,
                  selectedOptionId: optionId,
                  comment: obj.comment,
                }
              : obj
        ),
      }));
    } else {
      setSelectedTemplate((prevValues) => ({
        ...prevValues,
        appraisalQuestionAnswer: prevValues?.appraisalQuestionAnswer?.map(
          (obj) =>
            obj?.appraisalQuestions?.id === questionId
              ? {
                  ...obj,
                  selectedOptionId: "",
                  comment: obj.comment,
                }
              : obj
        ),
      }));
    }
  };

  const handleInputComment = (e, type, questionId) => {
    setSelectedTemplate((prevValues) => ({
      ...prevValues,
      appraisalQuestionAnswer: prevValues?.appraisalQuestionAnswer?.map((obj) =>
        obj?.appraisalQuestions?.id === questionId
          ? {
              ...obj,
              comment: e?.target?.value,
            }
          : obj
      ),
    }));
  };

  const handleCancelClick = () => {
    resetStates();
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      titleText: "Delete Appraisal ?",
      text: "Are you sure want to delete this appraisal?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      customClass: {
        popup: "custom-swal-width", // Custom width of the popup
        icon: "custom-swal-icon", // Custom icon size
        title: "custom-swal-title", // Custom title size
        content: "custom-swal-content", // Custom content (text) size
      },
    });

    if (result.isConfirmed) {
      await dispatch(deleteAppraisal(row.id)).unwrap();
      showNotification("success", "Appraisal deleted successfully");
    }
  };

  const handleEdit = (row) => {
    setEditTemplate(true);
    setViewTemplate(false);
    const selectedTemplatetoView = allAppraisals.find(
      (item) => item.id === row?.id
    );
    const ObjectToEdit = JSON.parse(JSON.stringify(selectedTemplatetoView));
    setSelectedTemplate(ObjectToEdit);
  };
  const handleDropdownChange = (name) => (option) => {
    setFormValues((prevValues) => ({ ...prevValues, [name]: option }));
  };

  const handleView = (row) => {
    setViewTemplate(true);
    const selectedTemplatetoView = allAppraisals.find(
      (item) => item.id === row?.id
    );
    setSelectedTemplate(selectedTemplatetoView);
  };

  const onClickAction = (actionType, row, id) => {
    if (actionType === "VIEW") {
      handleView(row);
    } else if (actionType === "EDIT") {
      handleEdit(row);
    } else if (actionType === "DELETE") {
      handleDelete(row);
    }
  };
  return (
    <>
      {stateOfAppraisal?.loading && <Loader />}
      {!editTemplate && !viewTemplate && (
        <>
          <div className="rounded-sm border border-none bg-white pt-4 pl-4">
            <div className="grid grid-cols-3 gap-x-4">
              <div className="max-w-xs">
                <Dropdown
                  submitRef={true}
                  defaultValue={formValues?.employee || null}
                  options={employeeOptions}
                  handleChange={handleDropdownChange("employee")}
                  errorMessage={""}
                  requiredField={false}
                  defaultLabel="Search by Employee name"
                  label=""
                  className="h-[39px]"
                  isClearable={true}
                />
              </div>
              <div className="max-w-xs">
                <Dropdown
                  submitRef={true}
                  defaultValue={formValues?.template || null}
                  options={templateOptions}
                  handleChange={handleDropdownChange("template")}
                  errorMessage={""}
                  requiredField={false}
                  defaultLabel="Search by Template Name"
                  label=""
                  className="h-[39px]"
                  isClearable={true}
                />
              </div>
              <div className="max-w-xs">
                <Dropdown
                  submitRef={true}
                  defaultValue={{ label: "Completed", value: "Completed" }}
                  options={[{ label: "Completed", value: "Completed" }]}
                  handleChange={handleDropdownChange("status")}
                  errorMessage={""}
                  requiredField={false}
                  defaultLabel="Search by Status"
                  label=""
                  className="h-[39px]"
                  disabled={false}
                  isClearable={false}
                />
              </div>
            </div>
          </div>
          <div className="mb-5">
            <GridTable
              onClickAction={onClickAction}
              actions={["EDIT", "VIEW", "DELETE"]}
              rowData={formattedRows}
              columnData={columns}
              toolTipName={"Create Staff"}
            />
          </div>
        </>
      )}
      {editTemplate && !viewTemplate && (
        <div className="rounded-sm border border-stroke bg-white shadow-default">
          <h1 className="text-center font-semibold text-white bg-primary py-3">
            {"Edit Employee Appraisal"}
          </h1>

          <div className="p-4">
            <div className="font-bold text-xl mb-2">
              {`${selectedTemplate?.appraisalTemplate?.templateTitle}`}
            </div>
            <div className="mb-10"></div>
            <div>
              {selectedTemplate?.appraisalTemplate?.appraisalQuestions?.map(
                (question, qIndex) => (
                  <div key={question.id} className="mb-5">
                    <div className="font-semibold text-base ml-2">
                      {`${qIndex + 1}. ${question.questionTitle}`}
                    </div>
                    <div className="ml-8">{question.questionSubTitle}</div>
                    <div className="mt-4 ml-12">
                      {question.questionOptions?.map(
                        (optionObj, optionIndex) => {
                          const optionValue = optionObj?.["optionTitle"];

                          return (
                            <p key={optionObj.id}>
                              <span className="font-medium mr-2">
                                {String.fromCharCode(65 + optionIndex)}.
                              </span>
                              {question.optionType === 1 ? (
                                <input
                                  type="checkbox"
                                  className="mr-2"
                                  checked={
                                    selectedTemplate?.appraisalQuestionAnswer?.find(
                                      (opt) =>
                                        opt?.selectedOptionId === optionObj?.id
                                    ) || false
                                  }
                                  onChange={(e) =>
                                    handleCheckBoxChange(
                                      e,
                                      question?.id,
                                      optionObj?.id
                                    )
                                  }
                                />
                              ) : (
                                <input
                                  type="radio"
                                  className="mr-2"
                                  name={`question-${question.id}`}
                                  checked={
                                    selectedTemplate?.appraisalQuestionAnswer?.some(
                                      (opt) =>
                                        opt?.selectedOptionId === optionObj?.id
                                    ) || false
                                  }
                                  onChange={(e) =>
                                    handleRadioChange(
                                      e,
                                      question?.id,
                                      optionObj?.id
                                    )
                                  }
                                />
                              )}
                              {optionValue}
                            </p>
                          );
                        }
                      )}
                      <div className="mt-5">
                        <p>Comments</p>
                        <textarea
                          className="block p-2.5 w-full h-12 text-sm text-gray-900 bg-gray-50 rounded-lg border-[1px] border-stroke focus:ring-primary focus:border-red-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                          placeholder="Write your comments.."
                          value={
                            selectedTemplate?.appraisalQuestionAnswer?.find(
                              (obj) =>
                                obj?.appraisalQuestions?.id === question?.id
                            )?.comment || ""
                          }
                          onChange={(e) =>
                            handleInputComment(
                              e,
                              question.optionType,
                              question?.id
                            )
                          }
                        ></textarea>
                      </div>
                    </div>
                  </div>
                )
              )}
              <div className="mt-10 border-t-2 border-stroke shadow-default">
                <p className="mt-5">Additional Comments</p>
                <textarea
                  className="block p-2.5 w-full h-12 text-sm text-gray-900 bg-gray-50 rounded-lg border-[1px] border-stroke focus:ring-primary focus:border-red-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  placeholder="Write your comments.."
                  onChange={(e) => {
                    setSelectedTemplate((prevValues) => ({
                      ...prevValues,
                      additionalComments: e.target.value,
                    }));
                  }}
                  value={selectedTemplate?.additionalComments || ""}
                ></textarea>
              </div>
            </div>
          </div>
          <div className="flex justify-center mt-5 pb-4">
            <div>
              <Button
                className="bg-red-600 mr-4"
                value="Cancel"
                handleClick={handleCancelClick}
              />
            </div>
            <div>
              <Button
                value="Edit Appraisal"
                disabled={stateOfAppraisal?.loading}
                handleClick={handleEditAppraisal}
              />
            </div>
          </div>
        </div>
      )}
      {!editTemplate && viewTemplate && (
        <div className="rounded-sm border border-stroke bg-white shadow-default">
          <h1 className="font-semibold text-white bg-primary py-3">
            <span className="ml-4">{`Template Details ID: ${selectedTemplate?.appraisalTemplate?.templateTitle}`}</span>
          </h1>
          <div className="p-4">
            <div className="font-bold text-xl mb-2">
              {`${selectedTemplate?.appraisalTemplate?.templateTitle}`}
            </div>
            <div className="mb-10"></div>
            <div>
              {selectedTemplate?.appraisalTemplate?.appraisalQuestions?.map(
                (question, qIndex) => (
                  <div key={question.id} className="mb-5">
                    <div className="font-semibold text-base ml-2">
                      {`${qIndex + 1}. ${question.questionTitle}`}
                    </div>
                    <div className="ml-8">{question.questionSubTitle}</div>
                    <div className="mt-4 ml-12">
                      {question.questionOptions?.map(
                        (optionObj, optionIndex) => {
                          const optionValue = optionObj?.["optionTitle"];

                          return (
                            <p key={optionObj.id}>
                              <span className="font-medium mr-2">
                                {String.fromCharCode(65 + optionIndex)}.
                              </span>
                              {question.optionType === 1 ? (
                                <input
                                  type="checkbox"
                                  className="mr-2"
                                  readOnly
                                  checked={
                                    selectedTemplate?.appraisalQuestionAnswer?.find(
                                      (opt) =>
                                        opt?.selectedOptionId === optionObj?.id
                                    ) || false
                                  }
                                  onChange={() => {
                                    showNotification(
                                      "info",
                                      "You're in view mode"
                                    );
                                  }}
                                />
                              ) : (
                                <input
                                  type="radio"
                                  className="mr-2"
                                  name={`question-${question.id}`}
                                  readOnly
                                  checked={
                                    selectedTemplate?.appraisalQuestionAnswer?.some(
                                      (opt) =>
                                        opt?.selectedOptionId === optionObj?.id
                                    ) || false
                                  }
                                  onChange={() => {
                                    showNotification(
                                      "info",
                                      "You're in view mode"
                                    );
                                  }}
                                />
                              )}
                              {optionValue}
                            </p>
                          );
                        }
                      )}
                      <div className="mt-5">
                        <p>Comments</p>
                        <textarea
                          className="block p-2.5 w-full h-12 text-sm text-gray-900 bg-gray-50 rounded-lg border-[1px] border-stroke focus:ring-primary focus:border-red-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                          placeholder="Write your comments.."
                          readOnly
                          value={
                            selectedTemplate?.appraisalQuestionAnswer?.find(
                              (obj) =>
                                obj?.appraisalQuestions?.id === question?.id
                            )?.comment || ""
                          }
                          onClick={() => {
                            showNotification("info", "You're in view mode");
                          }}
                        ></textarea>
                      </div>
                    </div>
                  </div>
                )
              )}
              <div className="mt-10 border-t-2 border-stroke shadow-default">
                <p className="mt-5">Additional Comments</p>
                <textarea
                  className="block p-2.5 w-full h-12 text-sm text-gray-900 bg-gray-50 rounded-lg border-[1px] border-stroke focus:ring-primary focus:border-red-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  placeholder="Write your comments.."
                  readOnly
                  value={selectedTemplate?.additionalComments || ""}
                  onClick={() => {
                    showNotification("info", "You're in view mode");
                  }}
                ></textarea>
              </div>
            </div>
          </div>
          <div className="flex justify-center pb-4">
            <Button value="Back" handleClick={handleCancelClick} />
          </div>
        </div>
      )}
    </>
  );
};

export default ManagerReviewedAppraisals;
